import { Check } from 'lucide-react';
import { LAYOUT_TEMPLATES, type LayoutTemplateId } from '@pageboss/shared';
import { Modal } from './modal';
import { cn } from '@/lib/utils';
import serviceBusinessPreview from '@/assets/templates/service-business.svg';
import restaurantPreview from '@/assets/templates/restaurant.svg';
import salonPreview from '@/assets/templates/salon.svg';
import contractorPreview from '@/assets/templates/contractor.svg';
import soloProPreview from '@/assets/templates/solo-pro.svg';
import minimalPreview from '@/assets/templates/minimal.svg';
import { Button } from '@/components/ui/button';

type UserPickableTemplate = Exclude<
  LayoutTemplateId,
  'legacy-stack' | 'starter-stack' | 'ai-generated' | 'sub-page-default'
>;

const PREVIEW_BY_ID: Record<UserPickableTemplate, string> = {
  'service-business': serviceBusinessPreview,
  restaurant: restaurantPreview,
  salon: salonPreview,
  contractor: contractorPreview,
  'solo-pro': soloProPreview,
  minimal: minimalPreview,
};

interface TemplatePickerModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (templateId: UserPickableTemplate) => void;
  onPickStarterStack: () => void;
  pendingTemplateId?: LayoutTemplateId | null;
}

export function TemplatePickerModal({
  open,
  onClose,
  onPick,
  onPickStarterStack,
  pendingTemplateId = null,
}: TemplatePickerModalProps) {
  const disabled = pendingTemplateId !== null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Pick a starting layout"
      description="Each layout is a starting point — you can rearrange or replace sections later."
      size="3xl"
      footer={
        <div className="flex items-center justify-between gap-4">
          <Button
            type="button"
            variant="link"
            onClick={onPickStarterStack}
            disabled={disabled}
            className="h-auto p-0 text-text-secondary hover:text-foreground"
          >
            None of these — start with a clean stack →
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={disabled}
            className="text-text-secondary"
          >
            Cancel
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {LAYOUT_TEMPLATES.map((template) => {
          const isPending = pendingTemplateId === template.id;
          const previewUrl = PREVIEW_BY_ID[template.id as UserPickableTemplate];
          return (
            <button
              key={template.id}
              type="button"
              disabled={disabled}
              onClick={() => onPick(template.id as UserPickableTemplate)}
              className={cn(
                'group relative flex flex-col overflow-hidden rounded-xl border border-border-subtle bg-card text-left transition-[border-color,box-shadow]',
                'hover:border-border hover:shadow-sm',
                'focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                isPending && 'border-brand-500 ring-2 ring-brand-500',
              )}
              aria-label={`Use ${template.label} layout`}
            >
              <div className="aspect-[10/7] w-full bg-surface-sunken">
                <img
                  src={previewUrl}
                  alt=""
                  className="h-full w-full object-contain p-3"
                  draggable={false}
                />
              </div>
              <div className="border-t border-border-subtle p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-foreground">{template.label}</div>
                  {isPending ? (
                    <Check className="h-4 w-4 shrink-0 text-brand-700" aria-label="Selected" />
                  ) : null}
                </div>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-text-secondary">
                  {template.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
